import { ReactElement, useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Container, Box } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchTrainingSeries,
  fetchLinkedServices,
  fetchCourses,
  selectTrainingSeriesById,
  selectCourses,
  selectCoursesStatus,
  selectLinkedServicesStatus,
  selectTrainingSeriesStatus,
} from '../manage.slice'
import { transformCoursesToRows } from './utils/course-transforms'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import DetailsHeaderActionButtons from '../components/edit-delete-buttons'
import { deleteTrainingSeries } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { TRAINING_SERIES_URLS } from '../constants'
import TrainingSeriesDetailsCard from './components/training-series-details-card'
import TrainingSeriesCoursesCard from './components/training-series-courses-card'

export default function TrainingSeriesDetailV4(): ReactElement {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let location = useLocation()
  let { trainingSeriesID } = useParams<{ trainingSeriesID: string }>()

  let [isDeleting, setIsDeleting] = useState<boolean>(false)
  let [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)

  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let trainingSeries = useAppSelector(
    selectTrainingSeriesById(trainingSeriesID),
  )
  let trainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let courseRows = useMemo(() => transformCoursesToRows(courses), [courses])

  let trainingSeriesCourses = useMemo(() => {
    if (!trainingSeries) return []
    return courseRows.filter((c) => trainingSeries.courseIDs.includes(c.id))
  }, [courseRows, trainingSeries])

  let linkedServiceName = useLinkedServiceName(trainingSeries?.linkedServiceID)

  let handleEdit = useCallback(() => {
    navigate(`${TRAINING_SERIES_URLS.INDEX}/${trainingSeriesID}/edit`)
  }, [navigate, trainingSeriesID])

  let handleDelete = useCallback(async () => {
    if (!trainingSeries) return

    setIsDeleting(true)

    await deleteTrainingSeries(trainingSeries.id)
      .then(() => {
        dispatch(fetchTrainingSeries())
        navigate(TRAINING_SERIES_URLS.INDEX, {
          state: {
            successMessage: `Training series "${trainingSeries.name}" deleted successfully`,
            linkedServiceID: trainingSeries.linkedServiceID,
          },
        })
      })
      .catch((err) => {
        let errorMessage = `Error deleting training series "${trainingSeries.name}".`
        // eslint-disable-next-line no-console
        console.error(
          errorMessage,
          'Training Series ID: ',
          trainingSeries.id,
          'Error: ',
          err,
        )
        showError(errorMessage)
      })
      .finally(() => {
        setIsDeleting(false)
        setIsDeleteModalOpen(false)
      })
  }, [trainingSeries, dispatch, navigate, showError])

  let isLoading =
    trainingSeriesStatus === 'pending' ||
    coursesStatus === 'pending' ||
    linkedServicesStatus === 'pending'

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(trainingSeriesStatus, fetchTrainingSeries)
  useAutoFetch(coursesStatus, fetchCourses)

  // Show success message from edit/create navigation
  useEffect(() => {
    let state = location.state as { successMessage?: string } | null
    if (state?.successMessage) {
      showSuccess(state.successMessage)
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate, showSuccess])

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex flex-wrap items-end justify-between gap-2 mb-4">
        <PageTitleWithLinkedService
          pageTitle={trainingSeries?.name ?? ''}
          moduleName="Training Series"
          linkedServiceName={linkedServiceName ?? ''}
          handleBackClick={() =>
            navigate(TRAINING_SERIES_URLS.INDEX, {
              state: { linkedServiceID: trainingSeries?.linkedServiceID },
            })
          }
          className="mb-0"
          isLoading={isLoading || !trainingSeries}
        />
        <DetailsHeaderActionButtons
          moduleName="Training Series"
          canSubmit={true}
          isSubmitting={false}
          isDisabled={isLoading || isDeleting}
          handleEdit={handleEdit}
          handleDelete={() => setIsDeleteModalOpen(true)}
        />
      </Box>
      <TrainingSeriesDetailsCard
        trainingSeries={trainingSeries || null}
        linkedServiceName={linkedServiceName}
        isLoading={isLoading}
      />
      <TrainingSeriesCoursesCard
        courses={trainingSeriesCourses}
        isLoading={isLoading}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        entityName="training series"
        entityDisplayName={trainingSeries?.name || ''}
        isSubmitting={isDeleting}
      />
      {NotificationComponent}
    </Container>
  )
}
